<template>
  <div class="pb-1 px-1">
    <v-card class="d-flex align-center mb-6 gap-3 pr-3" outlined rounded="lg">
      <div>
        <v-img
          :src="selectedParty?.cover"
          height="70"
          :width="(70 * 16) / 9"
          :aspect-ratio="16 / 9"
          class="rounded-l-lg"
        ></v-img>
      </div>
      <div class="flex-grow-1">
        <h6 class="mb-0">{{ selectedParty?.name }}</h6>
        <p class="mb-0">
          {{ selectedParty?.date | date("DD/MM/YYYY HH:mm") }}
        </p>
      </div>
      <v-btn @click="back" text>
        <v-icon left>mdi-arrow-left</v-icon>
        Mudar Evento
      </v-btn>
    </v-card>

    <h5>Seleione os lotes</h5>

    <v-alert v-if="error" type="error" class="mb-4">
      {{ error }}
    </v-alert>
    <v-expansion-panels focusable v-model="ticketGroupPanel">
      <v-expansion-panel
        v-for="(tg, i) in ticketGroupsSorted"
        :key="tg.id"
        :id="`ticket-group-${tg.id}`"
        :disabled="!tg.pending && !tg.lowestPrice"
        :style="
          tg.color
            ? `box-shadow: ${tg.color} 6px 0px 0px 0px inset !important`
            : undefined
        "
      >
        <v-expansion-panel-header>
          <div class="d-flex align-center gap-x-3 gap-y-1 flex-wrap">
            <div>
              <v-badge
                inline
                :content="ticketsPerGroup[tg.id]?.cart"
                :value="!!ticketsPerGroup[tg.id]?.cart"
              >
                <b class="text-16">{{ tg.name }}</b>
              </v-badge>
            </div>
            <v-scroll-y-transition>
              <div
                class="d-flex align-center gap-2"
                v-if="i != ticketGroupPanel"
              >
                <v-alert
                  v-if="tg.benefits"
                  text
                  color="primary"
                  dense
                  class="py-1 text-12 px-2 mb-0"
                >
                  <div class="d-flex gap-1">
                    <v-icon
                      v-for="(benefit, key) in tg.benefits"
                      color="primary"
                      :key="key"
                      x-small
                    >
                      {{ getBenefitIcon(key) }}
                    </v-icon>
                  </div>
                </v-alert>
              </div>
            </v-scroll-y-transition>
          </div>
          <v-scroll-y-transition leave-absolute>
            <div v-if="i != ticketGroupPanel" class="flex-grow-0">
              <span v-if="tg.lowestPrice" class="text-end text--disabled pr-4">
                A partir de
                {{ tg.lowestPrice | currency(true) }}
              </span>
              <span v-else-if="tg.pending" class="text-end text--disabled pr-4">
                Em breve
              </span>
              <span v-else class="text-end text--disabled pr-1">
                Esgotado
                <v-icon small class="text--disabled" right>
                  mdi-minus-circle
                </v-icon>
              </span>
            </div>
          </v-scroll-y-transition>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-alert
            v-if="tg.ageGroup"
            text
            dense
            color="primary"
            icon="mdi-account-file"
            class="mt-2"
          >
            O acesso a este setor é exclusivo para
            <strong>maiores de {{ tg.ageGroup }} anos.</strong>
          </v-alert>

          <div class="mt-3" v-if="tg.description">
            <p class="font-weight-bold mb-0">Descrição:</p>
            {{ tg.description }}
          </div>
          <div class="mt-3 d-flex flex-column" v-if="tg.benefits">
            <benefits-row :ticketGroup="tg" />
          </div>
          <v-divider class="my-2" v-if="tg.description" />

          <template v-if="tg.TicketBlock.length">
            <v-list>
              <v-list-item dense class="px-0">
                <h6 class="mb-0">Lotes</h6>
              </v-list-item>
              <v-list-item
                class="border-1 px-1"
                v-for="tb in tg.TicketBlock"
                :key="tb.id"
              >
                <v-list-item-content>
                  <div
                    class="d-flex justify-space-between gap-x-2 gap-y-1 flex-wrap"
                  >
                    <div>
                      <p class="mb-1 text-16 lh-5 font-weight-medium">
                        {{ tb.name }}
                      </p>
                      <div class="d-flex align-center">
                        <b>
                          <div
                            class="d-flex align-center gap-x-2 gap-y-1 flex-wrap"
                          >
                            <div class="d-flex flex-column">
                              <span
                                :class="{
                                  'text-decoration-line-through':
                                    tb.canUseMembership || tb.discount,
                                  'font-weight-medium':
                                    tb.canUseMembership || tb.discount,
                                  'text-16': tb.canUseMembership || tb.discount,
                                  'text-18':
                                    !tb.canUseMembership || !tb.discount,
                                  'text--disabled': tb.canUseMembership,
                                }"
                              >
                                {{ tb.price | currency(true) }}
                              </span>
                              <small
                                v-if="tb.fee"
                                class="text--secondary font-weight-medium"
                              >
                                Taxa{{ tb.feeStartsIn ? " a partir" : "" }}: +{{
                                  tb.fee | currency(true)
                                }}
                              </small>
                            </div>

                            <v-chip v-if="!!tg?.TableGroup" label small>
                              {{ tg.TableGroup.capacity }}
                              <!-- pessoa{{
                                tg.TableGroup.capacity > 1 ? "s" : ""
                              }} -->x
                              <!-- <v-icon small right left>
                                mdi-account{{
                                  tg.TableGroup.capacity > 1 ? "-group" : ""
                                }}
                              </v-icon> -->
                              <v-icon small right left>
                                mdi-ticket-account
                              </v-icon>
                            </v-chip>
                            <b
                              v-if="tb.discount"
                              class="font-weight-bold text-18 ml-2"
                            >
                              {{ (tb.price - tb.discount) | currency(true) }}
                            </b>

                            <template v-if="tb.alreadyUsed">
                              <v-chip
                                color="secondary"
                                small
                                label
                                @click="showPriceModal({ tb, tg })"
                              >
                                <v-icon x-small left>
                                  mdi-card-account-details-star
                                </v-icon>
                                Desconto já utilizado
                              </v-chip>
                            </template>
                            <template v-else-if="tb.canUseMembership">
                              <b class="font-weight-bold text-18 ml-2">
                                {{
                                  tb.membershipPrice[0].price | currency(true)
                                }}
                              </b>

                              <v-chip
                                :color="
                                  tb.membershipPrice[0].Membership
                                    .backgroundColor
                                "
                                small
                                text-color="white"
                                label
                                class="font-weight-medium text-14"
                                :style="{
                                  background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${
                                    tb.membershipPrice[0].Membership
                                      .backgroundImg
                                  }), ${
                                    tb.membershipPrice[0].Membership
                                      .backgroundColor || '#3333'
                                  }80`,
                                  backgroundSize: 'cover',
                                  border: `1px solid ${
                                    tb.membershipPrice[0].Membership
                                      .backgroundColor || '#000'
                                  }`,
                                  boxShadow: `0 0 10px  ${
                                    tb.membershipPrice[0].Membership
                                      .backgroundColor || 'rgba(0,0,0,.2)'
                                  }`,
                                }"
                                @click="showPriceModal({ tb, tg })"
                              >
                                <span
                                  :class="{
                                    'text-decoration-line-through':
                                      tb.alreadyUsed,
                                  }"
                                >
                                  {{ tb.membershipPrice[0].Membership.name }}
                                </span>
                                <v-icon v-if="tb.alreadyUsed" right>
                                  mdi-check
                                </v-icon>
                              </v-chip>
                            </template>

                            <template
                              v-else-if="
                                tb.membershipPrice && tb.membershipPrice.length
                              "
                            >
                              <v-chip
                                color="primary"
                                x-small
                                icon="mdi-card-account-details-star"
                                @click="showPriceModal({ tb, tg })"
                              >
                                {{
                                  tb.membershipPrice.length > 1
                                    ? "A partir de"
                                    : ""
                                }}
                                <b class="font-weight-bold text-12 mx-1">
                                  {{
                                    tb.membershipPrice[0].price | currency(true)
                                  }}
                                </b>

                                para
                                <b class="font-weight-bold text-12 mx-1">
                                  {{
                                    tb.membershipPrice.length > 1
                                      ? `socios`
                                      : tb.membershipPrice[0].Membership.name
                                  }}
                                </b>

                                <v-icon right small> mdi-chevron-down </v-icon>
                              </v-chip>
                              <!-- <span
                                class="font-weight-medium text-16 text--disabled ml-2"
                              >
                                Saiba Mais
                              </span> -->
                            </template>
                          </div>
                        </b>
                      </div>
                      <small
                        v-if="tb.useRequirement"
                        class="text-12 text--disabled"
                      >
                        {{ tb.useRequirement }}
                      </small>
                    </div>

                    <div
                      class="d-flex flex-column gap-2 justify-center align-end"
                    >
                      <template v-if="tg.TableGroup">
                        <v-btn
                          v-if="maxTickets === 1 || !cart[tb.id]?.quantity"
                          color="primary"
                          :loading="loading === tb.id"
                          @click="selectTable(tb, tg)"
                          :disabled="!canAddTicket(tg, tb)"
                        >
                          Selecionar Lugar
                        </v-btn>
                        <v-card
                          v-else
                          outlined
                          class="d-flex align-center gap-2 rounded-pill pa-1 align-self-end"
                        >
                          <v-btn icon @click="removeItem(tb.id)" small>
                            <v-icon>mdi-minus</v-icon>
                          </v-btn>
                          <b class="text-center">
                            {{ cart[tb.id].quantity }}
                          </b>
                          <v-btn
                            icon
                            @click="selectTable(tb, tg)"
                            small
                            :disabled="!canAddTicket(tg, tb)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-card>
                      </template>
                      <template v-else>
                        <v-btn
                          v-if="maxTickets === 1 || !cart[tb.id]?.quantity"
                          color="primary"
                          :loading="loading === tb.id"
                          @click="
                            buyTicket({
                              ticketGroup: tg,
                              ticketBlock: tb,
                            })
                          "
                          :disabled="!canAddTicket(tg, tb)"
                        >
                          {{ maxTickets === 1 ? "Comprar" : "Adicionar" }}
                        </v-btn>
                        <v-card
                          v-else
                          outlined
                          class="d-flex align-center gap-2 rounded-pill pa-1 align-self-end"
                        >
                          <v-btn icon @click="cart[tb.id].quantity--" small>
                            <v-icon>mdi-minus</v-icon>
                          </v-btn>
                          <b class="text-center">
                            {{ cart[tb.id].quantity }}
                          </b>
                          <v-btn
                            icon
                            @click="
                              buyTicket({
                                ticketGroup: tg,
                                ticketBlock: tb,
                              })
                            "
                            small
                            :disabled="!canAddTicket(tg, tb)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-card>
                      </template>
                    </div>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <template v-if="tg.kitList?.length > 0">
            <v-divider class="py-2 pb-0" v-if="tg.TicketBlock.length" />
            <h6 class="mb-0 mt-2">Pacotes</h6>
            <kits-selector :tg="tg" @buyKit="buyKit" />
          </template>

          <p
            v-if="!!Math.min(maxTickets, tg.maxTickets)"
            class="mb-0 text-center text-12 text--secondary"
          >
            Limite de
            {{ Math.min(maxTickets, tg.maxTickets) }} ingresso{{
              Math.min(maxTickets, tg.maxTickets) != 1 ? "s" : ""
            }}
            por pessoa para esse setor.
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
<script>
import VIRTUAL_POS from "@/services/staff/virtualPos";
import KitsSelector from "../../shop/KitsSelector.vue";
import BenefitsRow from "../../global/party/BenefitsRow.vue";
export default {
  name: "TicketsSelector",
  components: {
    KitsSelector,
    BenefitsRow,
  },
  props: {
    token: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    selectedParty: {
      type: Object,
    },
    cart: {
      type: Object,
    },
  },
  data() {
    return {
      ticketGroupPanel: null,
      ticketGroups: [],
      loading: false,
      error: null,
      statusOrder: ["sold-out", "available", "pending"],
    };
  },
  methods: {
    async fetchTickets() {
      try {
        if (!this.selectedParty) return;
        this.loading = true;
        this.error = null;
        const response = await VIRTUAL_POS.getTickets(
          this.token,
          this.selectedParty.id
        );
        this.ticketGroups = response.ticketGroups;
      } catch (e) {
        this.error = e.message || "Erro ao carregar eventos";
      } finally {
        this.loading = false;
      }
    },
    async buyTicket({ ticketBlock, ticketGroup, table }) {
      try {
        if (!this.cart[ticketBlock.id]) {
          const data = {
            ticketBlock,
            ticketGroup,
            tables: [],
            quantity: 0,
          };
          this.$set(this.cart, ticketBlock.id, data);
        }

        if (this.maxTickets === 1) this.$emit("next");

        const currentTicketBlock = this.cart[ticketBlock.id];
        this.$set(this.cart, ticketBlock.id, {
          ...currentTicketBlock,
          tables: table
            ? [...currentTicketBlock.tables, table]
            : currentTicketBlock.tables,
          quantity: currentTicketBlock.quantity + 1,
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    buyKit({ tg, kit }) {
      const hasItensInCart = Object.values(this.cart).some(
        (item) => item.quantity > 0
      );
      if (hasItensInCart) {
        const confirm = window.confirm(
          "Ao adicionar um pacote, todos os ingressos selecionados serão removidos do carrinho. Deseja continuar?"
        );
        if (!confirm) return;
      }

      // emiti a event for client.sync=true
      this.$emit("update:cart", {
        _kit: {
          quantity: 1,
          ticketBlock: {
            _isKit: true,
            ...kit,
          },
          ticketGroup: tg,
          kit: kit,
        },
      });
      this.$emit("next");
    },
    canAddTicket(tg, tb) {
      const {
        maxTickets: partyMaxTickets,
        ticketsPerGroup,
        partyTotal,
        hasConcessionTicket,
      } = this;

      // Verificação de meia entrada
      if (tb.concessionTicket && hasConcessionTicket) return false;

      // Verificação de limite de ingressos
      const maxTickets = Math.min(partyMaxTickets, tg.maxTickets);
      if (!maxTickets) return true;
      // Verificação de limite de ingressos por setor
      if (
        ticketsPerGroup[tg.id]?.total >= maxTickets ||
        partyTotal >= maxTickets
      )
        return false;
      return true;
    },
    processTicketBlock(tb) {
      return {
        ...tb,
        ...this.calculateFee(tb),
      };
    },
    calculateFee(tb) {
      var min = 0;
      var max = 0;
      Object.values(tb.fee).forEach((fee) => {
        if (min === 0) min = fee.amount;
        min = Math.min(min, fee.amount);
        max = Math.max(max, fee.amount);
      });
      return {
        fees: tb.fee,
        fee: min,
        feeStartsIn: min !== max,
      };
    },
    back() {
      this.$emit("select-party", null);
      this.$emit("prev");
    },
    getBenefitIcon(key) {
      const benefitsMap = {
        OPEN_BAR: "mdi-glass-mug-variant",
        OPEN_FOOD: "mdi-hamburger",
        SITTING_AREA: "mdi-seat",
        BATHROOMS: "mdi-toilet",
        AIR_CONDITIONING: "mdi-air-conditioner",
        LOCKERS: "mdi-locker",
        WIFI: "mdi-wifi",
        WAITER: "mdi-room-service",
        SAFE_SPACE: "mdi-heart-pulse",
      };
      return benefitsMap[key] || "mdi-star";
    },
  },
  computed: {
    ticketGroupsSorted() {
      const ticketGroups = this.ticketGroups
        .map((tg) => {
          return {
            ...tg,
            TableGroup: this.tableMap?.Groups.find(
              ({ id }) => id === tg.tableGroupId
            ),
            TicketBlock: tg.TicketBlock.map((tb) =>
              this.processTicketBlock(tb, tg)
            ).sort((a, b) => {
              const aStatus = this.statusOrder.indexOf(a.status);
              const bStatus = this.statusOrder.indexOf(b.status);
              if (aStatus === bStatus) {
                return 0;
              }
              return aStatus - bStatus;
            }),
            kitList: tg.KitList.map((kit) => {
              return {
                ...kit,
                status: "available",
                ...this.calculateFee(kit),
              };
            }),

            lowestPrice:
              [...tg.TicketBlock, ...tg.KitList].sort(
                (a, b) => (a.price || 0) - (b.price || 0)
              )[0]?.price || null,
          };
        })
        .sort((a, b) => {
          // sort by status (available first, then pending, then sold-out) and then by name
          const aIsAvailable = a.lowestPrice;
          const bIsAvailable = b.lowestPrice;
          if (aIsAvailable && !bIsAvailable) return -1;
          if (!aIsAvailable && bIsAvailable) return 1;

          const aIsPending = a.pending && !a.lowestPrice;
          const bIsPending = b.pending && !b.lowestPrice;
          if (aIsPending && !bIsPending) return 1;
          if (!aIsPending && bIsPending) return -1;

          return a.order - b.order;
        });

      if (ticketGroups.length > 0 && ticketGroups[0].lowestPrice)
        this.ticketGroupPanel = 0;

      return ticketGroups;
    },
    ticketsPerGroup() {
      return Object.values(this.cart).reduce(
        (acc, { quantity, ticketGroup }) => {
          if (!acc[ticketGroup.id])
            acc[ticketGroup.id] = { cart: 0, user: 0, total: 0 };
          acc[ticketGroup.id].cart += quantity;
          acc[ticketGroup.id].total += quantity;
          return acc;
        },
        {}
      );
    },
    maxTickets() {
      return this.selectedParty?.posMaxTickets;
    },
  },
  watch: {
    selectedParty: {
      handler(val) {
        if (!val) return;
        this.ticketGroups = [];
        this.fetchTickets();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchTickets();
  },
};
</script>
